import { create } from 'zustand';
import { fetchMobileOrderStatus } from 'lib/apis';
import { MOBILE_ACTIVE_STATE } from 'lib/constants/order';
import { LOGIN_TYPE } from 'lib/constants/login';
import toast from 'lib/utils/toast';
import { COMMON_MSG, ERROR_MSG } from 'lib/constants/error';
import { currentUrlParams } from 'lib/utils/url';
import storage from 'lib/utils/storage';
import { STORAGE } from 'lib/utils/storage/constants';
import { validMobile } from 'lib/utils/util';
import { addReportPublicParams } from 'lib/utils/dataReport';

interface MobileStore {
  inputMobile: string; // 手机号输入框填写的手机号
  isAllowed: boolean; // 表示是否已订购，如果已订购则是false, 其他则为true
  activeState: MOBILE_ACTIVE_STATE; // 如果是带登录态订购则无需激活返回0；1，2表示无登录态订购
  queryMobile: string; // 执行查询过的手机号
  mobileBindLoginType: LOGIN_TYPE; // 手机号绑定过账号类型，1微信 2 QQ 0 未绑定
  gatewayMobile: string; // 网关获取的手机号（掩码手机号）
  useGatewayMobile: boolean; // 是否使用网关手机号
  updateInputMobile: (mobile: string) => void;
  queryMobileOrderStatus: (
    mobile: string,
  ) => Promise<{ isAllowed?: boolean; activeState?: MOBILE_ACTIVE_STATE; mobileBindLoginType?: number } | null>;
  updateGatewayMobile: (gatewayMobile: string, useGatewayMobile?: boolean) => void;
  getInputValueToShow: () => string;
}

/**
 * 1、初始化手机号, extraMobile 是从外部传入的手机号,包含不限于沧州大图 （已有逻辑无法去除）
 * 2、flowmobile 是从短信传入的手机号（已有逻辑无法去除）
 * 3、mobileKey 是从链接上通用的mobileKey
 * */
const getInitMobile = () => {
  const storageMobile = storage.getStorage(STORAGE.ORDER_MOBILE);
  if (validMobile(storageMobile)) {
    return storageMobile;
  }
  // 获取链接上通用的mobileKey
  const mobileKey = currentUrlParams.getCurrentParams('mobileKey');
  // 顺序无所谓，同一时间只会有一个生效
  const mobileKeyList = ['extraMobile', 'flowmobile', mobileKey || 'phone'];

  for (const key of mobileKeyList) {
    const mobile = currentUrlParams.getCurrentParams(key);
    if (validMobile(mobile)) {
      return mobile;
    }
  }
  return '';
};

const initializeMobile = getInitMobile();

export const useMobileStore = create<MobileStore>((set, get) => ({
  inputMobile: ((initializeMobile as string) || '').replace(/\D/g, ''),
  isAllowed: true,
  activeState: MOBILE_ACTIVE_STATE.NO_NEED_ACTIVE,
  queryMobile: '',
  mobileBindLoginType: 0,
  gatewayMobile: '',
  useGatewayMobile: false,
  updateInputMobile: (inputMobile: string) => {
    addReportPublicParams({
      mobile: inputMobile,
      gatewayMobile: '',
    });
    set({
      inputMobile,
      useGatewayMobile: false, // 用户手动输入手机号后不再使用网关手机号
    });
  },
  queryMobileOrderStatus: async (mobile: string) => {
    toast.loading();
    const res = await fetchMobileOrderStatus({
      mobile,
    });
    if (res?.code !== 0) {
      toast.info(ERROR_MSG[res?.code] || COMMON_MSG);
      return null;
    }
    toast.close();
    const { isAllowed, activeState, userType } = res?.data;
    set({
      isAllowed,
      activeState,
      queryMobile: mobile,
      mobileBindLoginType: userType,
    });

    return {
      isAllowed,
      activeState,
      mobileBindLoginType: userType,
    };
  },
  updateGatewayMobile: (gatewayMobile: string, useGatewayMobile = true) => {
    const { inputMobile } = get();
    if (validMobile(inputMobile)) {
      return;
    }
    addReportPublicParams({ gatewayMobile });
    set({
      gatewayMobile,
      useGatewayMobile,
    });
  },
  getInputValueToShow: () => {
    const { useGatewayMobile, gatewayMobile, inputMobile } = get();
    if (useGatewayMobile && gatewayMobile) {
      return gatewayMobile;
    }
    return inputMobile;
  },
}));
